"use client";
import { PublicSurvey } from "@/types/publicSurvey";
import type { SurveyQuestion } from "@/types/survey";
import { DEFAULT_MAX_RANK } from "@/types/survey";
import { useParams } from "next/navigation";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { getMomentusOptionsConfig } from "@/lib/momentusOptions";
import { motion, AnimatePresence } from "motion/react";
import { LoadingSpinner } from "@/components/survey-questions";
import {
  SurveyBrandFooter,
  SurveyBrandHeader,
  SurveyRenderer,
  getBrandColor,
  getLogoUrl,
  getSurveyTheme,
  useSurveyPagination,
} from "@/components/survey-renderer";
import surveyTheme from "@/components/survey-renderer/surveyTheme.module.css";
import { Button, toast } from "@/design-system";
import { shouldRenderQuestion as evaluateShouldRenderQuestion } from "@/lib/questionConditions";
import {
  PUBLIC_SURVEY_UI_COPY,
  SURVEY_BASE_LANGUAGE,
  SURVEY_LANGUAGE_BY_CODE,
  getSurveyLanguageFallbackMessage,
  isSurveyLanguageCode,
  type SurveyLanguageCode,
} from "@/lib/surveyLanguages";
import {
  SELECT_OTHER_TEXT_KEY,
  SELECT_OTHER_VALUE,
  decodeOtherTextValue,
  hasOtherAnswerText,
  isBlankOtherAnswer,
  isOtherAnswer,
  toOtherAnswer,
} from "@/lib/selectOtherOption";
import { getMultiSelectMaxSelections } from "@/lib/multiSelectOptions";
import { normalizeEmailAddress } from "@/utils/email";
import styles from "./page.module.css";

type PublicSurveyActivityName =
  | "survey.public.page_view"
  | "survey.public.view_duration"
  | "survey.public.click"
  | "survey.public.answer_changed"
  | "survey.public.contact_submitted"
  | "survey.public.response_saved"
  | "survey.public.submitted"
  | "survey.public.error"
  | "survey.public.review_link_clicked"
  | "survey.public.language_auto_selected"
  | "survey.public.language_changed";

type PublicSurveyActivityEvent = {
  name: PublicSurveyActivityName;
  timestamp: string;
  displayText?: string;
  payload: Record<string, unknown>;
};

const PUBLIC_SURVEY_ACTIVITY_FLUSH_INTERVAL_MS = 10_000;
const PUBLIC_SURVEY_ACTIVITY_MAX_BATCH_SIZE = 20;
const PUBLIC_SURVEY_MIN_VIEW_DURATION_MS = 1_000;
const PUBLIC_SURVEY_ACTIVITY_EVENT_LIMIT = 240;
const OBJECT_ID_PATTERN = /\b[a-f0-9]{24}\b/gi;
const SURVEY_LANGUAGE_PREFERENCE_KEY = "surveyLanguage";
const SURVEY_TRANSLATION_POLL_INTERVAL_MS = 12_000;
const conditionalQuestionTransition = {
  height: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
  opacity: { duration: 0.18, ease: "easeOut" },
  filter: { duration: 0.22, ease: "easeOut" },
} as const;

type MomentusSurveyPayload = PublicSurvey & { surveyResponseId: string };

type SurveyLanguageSelectionSource = "query" | "saved" | "browser" | "default";

function normalizeSurveyLanguageCandidate(value: unknown): SurveyLanguageCode | null {
  if (typeof value !== "string") return null;
  const normalized = value.trim().toLowerCase().split(/[-_]/)[0];
  return isSurveyLanguageCode(normalized) ? normalized : null;
}

function parseCompactAnswerMap(raw: unknown): Record<string, string> {
  const map: Record<string, string> = {};
  if (typeof raw !== "string" || !raw) return map;
  for (const part of raw.split(";")) {
    const separatorIndex = part.indexOf("=");
    if (separatorIndex === -1) continue;
    const key = part.slice(0, separatorIndex);
    const value = part.slice(separatorIndex + 1);
    if (key) map[key] = value;
  }
  return map;
}

function compactRowsAnswered(
  raw: unknown,
  options: { requireOtherText?: boolean } = {},
) {
  const valueByKey = parseCompactAnswerMap(raw);
  const otherText = decodeOtherTextValue(valueByKey[SELECT_OTHER_TEXT_KEY]);
  const rows = new Set<string>();
  for (const [rowValue, rankValue] of Object.entries(valueByKey)) {
    if (rowValue === SELECT_OTHER_TEXT_KEY || !rankValue.trim()) continue;
    if (
      options.requireOtherText &&
      rowValue === SELECT_OTHER_VALUE &&
      !hasOtherAnswerText(otherText)
    ) {
      continue;
    }
    rows.add(rowValue);
  }
  return rows;
}

function sanitizePublicSurveyText(value: unknown, maxLength = PUBLIC_SURVEY_ACTIVITY_EVENT_LIMIT) {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength)
    .replace(/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, "[redacted-email]")
    .replace(/\b(?:\+?1[-.\s]?)?(?:\(?\d{3}\)?[-.\s]?)\d{3}[-.\s]?\d{4}\b/g, "[redacted-phone]")
    .replace(OBJECT_ID_PATTERN, "[id]");
}

function sanitizePublicSurveyPath(path: string) {
  return (path.split("?")[0]?.split("#")[0] || "/").replace(OBJECT_ID_PATTERN, "[id]");
}

function getPublicSurveyElementLabel(element: HTMLElement) {
  const explicitLabel =
    element.getAttribute("data-activity-label") ||
    element.getAttribute("aria-label") ||
    element.getAttribute("title");
  if (explicitLabel) return sanitizePublicSurveyText(explicitLabel, 120);

  const text = element.innerText || element.textContent || "";
  return sanitizePublicSurveyText(text, 120);
}

export default function SurveyPage() {
  // fetch the survey by the current url
  const { organizationName, surveyUrlName } = useParams();
  const [survey, setSurvey] = useState<PublicSurvey | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [surveyResponseId, setSurveyResponseId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [fetchRetry, setFetchRetry] = useState(0);

  type AnswerValue = string | number | boolean | string[];
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({});
  const answersRef = React.useRef<Record<string, AnswerValue>>({});
  const trackedViewRef = React.useRef<string | null>(null);
  const activityQueueRef = React.useRef<PublicSurveyActivityEvent[]>([]);
  const activityFlushInFlightRef = React.useRef(false);
  const activeViewRef = React.useRef<{ route: string; startedAt: number } | null>(null);
  const surveyRef = React.useRef<PublicSurvey | null>(null);
  const isLoadingRef = React.useRef(isLoading);
  const surveyResponseIdRef = React.useRef<string | null>(null);
  const responseCreationPromiseRef = React.useRef<Promise<string | null> | null>(null);
  const momentusInitializationRef = React.useRef<{ surveyId: string; language: string; promise: Promise<MomentusSurveyPayload> } | null>(null);
  const momentusOptionsMutation = useMutation({
    mutationFn: async ({ surveyId, ...body }: {
      surveyId: string;
      action: "initialize_momentus_options" | "retry_momentus_options";
      surveyResponseId?: string;
      questionId?: string;
      responseLanguage: string;
      trackedLink?: string;
    }): Promise<MomentusSurveyPayload> => {
      const response = await fetch(`/api/survey-public/${surveyId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify(body),
      });
      if (!response.ok) throw new Error("Couldn’t load the survey’s event options. Please try again.");
      return response.json();
    },
    retry: false,
  });
  const submitQueueRef = React.useRef(Promise.resolve());
  const pendingHiddenQuestionIdsRef = React.useRef<Set<string>>(new Set());
  const contactSubmitInFlightRef = React.useRef(false);
  const publicSurveyActivityPageViewRef = React.useRef<string | null>(null);
  const languageAutoSelectionTrackedRef = React.useRef<string | null>(null);
  const previousEffectiveLanguageRef = React.useRef<SurveyLanguageCode | null>(null);
  const languageMenuRef = React.useRef<HTMLDetailsElement | null>(null);
  const [selectedLanguage, setSelectedLanguage] =
    useState<SurveyLanguageCode>(SURVEY_BASE_LANGUAGE);
  const [languageRefreshNonce, setLanguageRefreshNonce] = useState(0);

  // Helpers for query params and storage
  const getQueryParam = React.useCallback((key: string): string | null => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const v = params.get(key);
    return v ? v.split("?")[0].split("&")[0] : null;
  }, []);

  const getTrackedLinkSlug = React.useCallback(
    (): string | null => getQueryParam("trackedLink"),
    [getQueryParam],
  );

  // Only read survey response id from URL for access validation
  const getResponseIdFromUrl = React.useCallback((): string | null => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    return (
      params.get("surveyResponseId") ||
      params.get("responseId") ||
      params.get("responseID")
    );
  }, []);

  // Deprecated: Previously used for localStorage keying; now cookies only
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const getPerSurveyStorageKey = (_id: string | undefined | null) =>
    null as string | null;

  // Contact info gating state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [contactInfoSubmitted, setContactInfoSubmitted] = useState(false);
  const [submittingContactInfo, setSubmittingContactInfo] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const setCookie = useCallback((
    ck: string,
    value: string,
    days = 30,
    path: string = "/"
  ) => {
    if (typeof document === "undefined") return;
    const expires = new Date();
    expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
    document.cookie = `${ck}=${encodeURIComponent(
      value
    )}; expires=${expires.toUTCString()}; path=${path}`;
  }, []);
  const getCookie = useCallback((ck: string) => {
    if (typeof document === "undefined") return null;
    const value = document.cookie
      .split("; ")
      .find((row) => row.startsWith(`${ck}=`));
    return value ? decodeURIComponent(value.split("=")[1]) : null;
  }, []);

  const loadSurveyResponseId = useCallback((): string | null => {
    // Prefer URL params
    const fromQuery =
      getQueryParam("surveyResponseId") ||
      getQueryParam("responseId") ||
      getQueryParam("responseID");
    if (fromQuery) return fromQuery;

    // Then cookie
    const fromCookie = getCookie("surveyResponseId");
    if (fromCookie) return fromCookie;

    // Nothing found
    return null;
  }, [getCookie, getQueryParam]);

  const getRequestedSurveyLanguage = useCallback((): {
    language: SurveyLanguageCode;
    source: SurveyLanguageSelectionSource;
  } => {
    const queryLanguage =
      normalizeSurveyLanguageCandidate(getQueryParam("lang")) ||
      normalizeSurveyLanguageCandidate(getQueryParam("language"));
    if (queryLanguage) return { language: queryLanguage, source: "query" };

    let savedLanguage: SurveyLanguageCode | null = null;
    if (typeof window !== "undefined") {
      try {
        savedLanguage = normalizeSurveyLanguageCandidate(
          window.localStorage.getItem(SURVEY_LANGUAGE_PREFERENCE_KEY),
        );
      } catch {}
    }
    savedLanguage =
      savedLanguage || normalizeSurveyLanguageCandidate(getCookie(SURVEY_LANGUAGE_PREFERENCE_KEY));
    if (savedLanguage) return { language: savedLanguage, source: "saved" };

    if (typeof navigator !== "undefined") {
      const browserLanguages = [
        ...(Array.isArray(navigator.languages) ? navigator.languages : []),
        navigator.language,
      ];
      for (const browserLanguage of browserLanguages) {
        const normalizedBrowserLanguage = normalizeSurveyLanguageCandidate(browserLanguage);
        if (normalizedBrowserLanguage && normalizedBrowserLanguage !== SURVEY_BASE_LANGUAGE) {
          return { language: normalizedBrowserLanguage, source: "browser" };
        }
      }
    }

    return { language: SURVEY_BASE_LANGUAGE, source: "default" };
  }, [getCookie, getQueryParam]);

  const persistSurveyLanguagePreference = useCallback((language: SurveyLanguageCode) => {
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(SURVEY_LANGUAGE_PREFERENCE_KEY, language);
      } catch {}
    }
    setCookie(SURVEY_LANGUAGE_PREFERENCE_KEY, language, 365, "/");
  }, [setCookie]);

  const getOrCreateSurveyVisitorId = useCallback(() => {
    if (typeof window === "undefined") return null;
    const storageKey = "surveyVisitorId";
    let existing: string | null = null;
    try {
      existing = window.localStorage.getItem(storageKey);
    } catch {}
    existing = existing || getCookie(storageKey);
    if (existing) {
      setCookie(storageKey, existing, 365, "/");
      return existing;
    }
    const generated =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    try {
      window.localStorage.setItem(storageKey, generated);
    } catch {}
    setCookie(storageKey, generated, 365, "/");
    return generated;
  }, [getCookie, setCookie]);

  const getSurveyId = useCallback(() => {
    const surveyRecord = survey?.survey as
      | (PublicSurvey["survey"] & { debugId?: string | null })
      | undefined;
    return surveyRecord?.id || surveyRecord?._id || surveyRecord?.debugId || null;
  }, [survey?.survey]);

  useEffect(() => {
    surveyResponseIdRef.current = surveyResponseId;
  }, [surveyResponseId]);

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    surveyRef.current = survey;
  }, [survey]);

  useEffect(() => {
    isLoadingRef.current = isLoading;
  }, [isLoading]);

  const flushPublicSurveyActivity = useCallback(async (keepalive = false) => {
    const surveyId = getSurveyId();
    if (!surveyId || activityFlushInFlightRef.current || activityQueueRef.current.length === 0) return;

    const events = activityQueueRef.current.splice(0, PUBLIC_SURVEY_ACTIVITY_MAX_BATCH_SIZE);
    activityFlushInFlightRef.current = true;

    try {
      const response = await fetch(`/api/survey-public/${surveyId}/track-activity`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        keepalive,
        body: JSON.stringify({
          visitorId: getOrCreateSurveyVisitorId(),
          surveyResponseId: loadSurveyResponseId() || surveyResponseIdRef.current || undefined,
          events,
        }),
      });
      if (!response.ok && response.status !== 400 && response.status !== 404) {
        activityQueueRef.current = [...events, ...activityQueueRef.current].slice(
          0,
          PUBLIC_SURVEY_ACTIVITY_MAX_BATCH_SIZE * 3,
        );
      }
    } catch {
      activityQueueRef.current = [...events, ...activityQueueRef.current].slice(
        0,
        PUBLIC_SURVEY_ACTIVITY_MAX_BATCH_SIZE * 3,
      );
    } finally {
      activityFlushInFlightRef.current = false;
    }
  }, [getOrCreateSurveyVisitorId, getSurveyId, loadSurveyResponseId]);

  const trackPublicSurveyActivity = useCallback((
    name: PublicSurveyActivityName,
    payload: Record<string, unknown> = {},
    displayText?: string,
  ) => {
    if (typeof window === "undefined") return;

    const safePayload: Record<string, unknown> = {
      route: sanitizePublicSurveyPath(window.location.pathname),
      ...payload,
    };

    activityQueueRef.current.push({
      name,
      timestamp: new Date().toISOString(),
      displayText: displayText ? sanitizePublicSurveyText(displayText, 240) : undefined,
      payload: safePayload,
    });

    if (activityQueueRef.current.length >= PUBLIC_SURVEY_ACTIVITY_MAX_BATCH_SIZE) {
      void flushPublicSurveyActivity();
    }
  }, [flushPublicSurveyActivity]);

  const endActivePublicSurveyView = useCallback((reason: "route_change" | "hidden" | "pagehide") => {
    const activeView = activeViewRef.current;
    if (!activeView) return;

    activeViewRef.current = null;
    const durationMs = Date.now() - activeView.startedAt;
    if (durationMs < PUBLIC_SURVEY_MIN_VIEW_DURATION_MS) return;

    trackPublicSurveyActivity(
      "survey.public.view_duration",
      {
        route: activeView.route,
        durationMs,
        durationSeconds: Math.round(durationMs / 1000),
        reason,
      },
      `Viewed public survey for ${Math.max(1, Math.round(durationMs / 1000))}s`,
    );
  }, [trackPublicSurveyActivity]);

  useEffect(() => {
    const savedName = getCookie("surveyUserName");
    const savedEmail = getCookie("surveyUserEmail");
    if (savedName) setName(savedName);
    if (savedEmail) setEmail(normalizeEmailAddress(savedEmail));
  }, [getCookie]);

  const selectedSurveyLanguage =
    survey?.language?.selectedLanguage ?? selectedLanguage;
  const effectiveSurveyLanguage =
    survey?.language?.effectiveLanguage ?? SURVEY_BASE_LANGUAGE;
  const surveyUiCopy =
    PUBLIC_SURVEY_UI_COPY[effectiveSurveyLanguage] ?? PUBLIC_SURVEY_UI_COPY.en;
  const languageFallbackMessage = survey?.language
    ? getSurveyLanguageFallbackMessage(
        selectedSurveyLanguage,
        survey.language.fallbackReason ?? null,
      )
    : null;

  const validateEmail = (emailValue: string) => {
    const normalizedEmail = normalizeEmailAddress(emailValue);
    if (!normalizedEmail) return surveyUiCopy.emailRequired;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(normalizedEmail))
      return surveyUiCopy.emailInvalid;
    return "";
  };

  // A survey is "repeatable" only when it both allows multiple submissions
  // AND is public. Non-public surveys always reuse their issued surveyResponseId.
  const allowMultipleSubmissions = Boolean(
    (
      survey as unknown as {
        survey?: { allowMultipleSubmissions?: boolean; isPublic?: boolean };
      }
    )?.survey?.allowMultipleSubmissions &&
      (survey as unknown as { survey?: { isPublic?: boolean } })?.survey
        ?.isPublic
  );

  const hasExistingResponseId = useMemo(() => {
    return Boolean(loadSurveyResponseId() || surveyResponseId);
  }, [loadSurveyResponseId, surveyResponseId]);

  const isAnonymousSurvey = survey?.survey?.isAnonymous === true;
  const momentusDraftNeedsContact = Boolean(survey?.survey?.isPublic &&
    (survey.allQuestions || survey.questions).some((question) => question.momentusOptions) &&
    !(survey.name && survey.email));
  const shouldShowContactFields = useMemo(() => {
    if (isAnonymousSurvey) return false;
    if (momentusDraftNeedsContact) return true;
    if (hasExistingResponseId) return false;
    return true;
  }, [isAnonymousSurvey, hasExistingResponseId, momentusDraftNeedsContact]);

  // Access control: allow if survey is public; otherwise require a response id in the URL
  const hasUrlSurveyResponseId = useMemo(() => {
    return Boolean(getResponseIdFromUrl());
  }, [getResponseIdFromUrl]);

  const isAccessAllowed = useMemo(() => {
    if (!survey) return false;
    const { isPublic } = survey.survey;
    if (isPublic) return true;
    return hasUrlSurveyResponseId;
  }, [survey, hasUrlSurveyResponseId]);

  const isContactInfoValid = useMemo(() => {
    if (!shouldShowContactFields) return true;
    const nameValid = name && name.trim().length > 0;
    const emailValid =
      email && email.trim().length > 0 && (!emailTouched || !emailError);
    return Boolean(nameValid && emailValid);
  }, [shouldShowContactFields, name, email, emailTouched, emailError]);

  const handleEmailBlur = () => {
    setEmail(normalizeEmailAddress(email));
    setEmailTouched(true);
    setEmailError(validateEmail(email));
  };

  useEffect(() => {
    if (emailTouched && emailError) setEmailError("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, email]);

  const persistSurveyResponseId = (id: string) => {
    try {
      if (typeof window === "undefined") return;
      // Persist as a cookie scoped to the current path
      const currentPath =
        typeof window !== "undefined" ? window.location.pathname : "/";
      setCookie("surveyResponseId", id, 30, currentPath);
      // Best-effort: Remove any previous root-path cookie to avoid ambiguity when reading
      setCookie("surveyResponseId", "", -1, "/");
    } catch {}
  };

  const clearPersistedSurveyResponseId = useCallback(() => {
    try {
      if (typeof window === "undefined") return;
      const currentPath = window.location.pathname;
      setCookie("surveyResponseId", "", -1, currentPath);
      setCookie("surveyResponseId", "", -1, "/");
      const url = new URL(window.location.href);
      if (
        url.searchParams.has("surveyResponseId") ||
        url.searchParams.has("responseId") ||
        url.searchParams.has("responseID")
      ) {
        url.searchParams.delete("surveyResponseId");
        url.searchParams.delete("responseId");
        url.searchParams.delete("responseID");
        window.history.replaceState(
          window.history.state,
          "",
          `${url.pathname}${url.search}${url.hash}`
        );
      }
    } catch {}
  }, [setCookie]);

  const getCompletedAtFromSurveyData = (data: unknown): string | null => {
    const getNested = (obj: unknown, key: string): unknown => {
      if (!obj || typeof obj !== "object") return undefined;
      return (obj as Record<string, unknown>)[key];
    };
    const getCompletedAtFrom = (obj: unknown): string | null => {
      const v = getNested(obj, "completedAt");
      return typeof v === "string" && v ? v : null;
    };
    return (
      getCompletedAtFrom(data) ||
      getCompletedAtFrom(getNested(data, "surveyResponse")) ||
      getCompletedAtFrom(getNested(data, "response")) ||
      getCompletedAtFrom(getNested(data, "survey"))
    );
  };

  useEffect(() => {
    let cancelled = false;
    const fetchSurvey = async () => {
      try {
        const orgPart = String(organizationName ?? "");
        const namePart = String(surveyUrlName ?? "");
        let modifiedSurveyUrlName = orgPart + "_" + namePart;
        modifiedSurveyUrlName = modifiedSurveyUrlName.replace(/\//g, "_");
        const languageRequest = getRequestedSurveyLanguage();
        setSelectedLanguage(languageRequest.language);

        // First fetch the survey definition without a cached response id. If the
        // survey is private, retry below with the URL-issued id.
        const baseParams = new URLSearchParams();
        baseParams.set("surveyUrl", modifiedSurveyUrlName);
        baseParams.set("lang", languageRequest.language);
        const urlResponseId = getResponseIdFromUrl();
        const baseUrl = `/api/survey-public/find-survey?${baseParams.toString()}`;

        let baseRes = await fetch(baseUrl, {
          headers: { "Content-Type": "application/json" },
          credentials: "omit",
          cache: "no-store",
        });

        if (!baseRes.ok && urlResponseId) {
          const paramsWithUrlSrid = new URLSearchParams(baseParams);
          paramsWithUrlSrid.set("surveyResponseId", urlResponseId);
          const urlWithUrlSrid = `/api/survey-public/find-survey?${paramsWithUrlSrid.toString()}`;
          baseRes = await fetch(urlWithUrlSrid, {
            headers: { "Content-Type": "application/json" },
            credentials: "omit",
            cache: "no-store",
          });
        }

        if (!baseRes.ok) {
          const errorText = await baseRes.text();
          console.error(
            "API Error:",
            baseRes.status,
            baseRes.statusText,
            errorText
          );
          throw new Error(
            baseRes.status === 404
              ? "Survey not found"
              : `API Error: ${baseRes.status} ${baseRes.statusText}`,
          );
        }

        let surveyData = (await baseRes.json()) as unknown as PublicSurvey;
        if (cancelled) return;

        // Decide whether to attach previous response data based on allowMultipleSubmissions.
        // Repeatable behaviour only applies to public surveys; non-public surveys always
        // reuse their issued surveyResponseId regardless of the toggle.
        const fetchedAllowMultiple = Boolean(
          (
            surveyData as unknown as {
              survey?: {
                allowMultipleSubmissions?: boolean;
                isPublic?: boolean;
              };
            }
          )?.survey?.allowMultipleSubmissions &&
            (
              surveyData as unknown as {
                survey?: { isPublic?: boolean };
              }
            )?.survey?.isPublic
        );

        if (fetchedAllowMultiple) {
          const foundSrid = loadSurveyResponseId();
          if (foundSrid) {
            const paramsWithSrid = new URLSearchParams();
            paramsWithSrid.set("surveyUrl", modifiedSurveyUrlName);
            paramsWithSrid.set("lang", languageRequest.language);
            paramsWithSrid.set("surveyResponseId", foundSrid);
            const urlWithSrid = `/api/survey-public/find-survey?${paramsWithSrid.toString()}`;
            const resWithSrid = await fetch(urlWithSrid, {
              headers: { "Content-Type": "application/json" },
              credentials: "omit",
              cache: "no-store",
            });
            if (resWithSrid.ok) {
              const candidateData = (await resWithSrid.json()) as unknown as PublicSurvey;
              if (cancelled) return;
              const candidateResponseId = (
                candidateData as PublicSurvey & { surveyResponseId?: string }
              ).surveyResponseId;
              if (candidateResponseId && !getCompletedAtFromSurveyData(candidateData)) {
                surveyData = candidateData;
              } else {
                clearPersistedSurveyResponseId();
                setSurveyResponseId(null);
              }
            } else {
              clearPersistedSurveyResponseId();
              setSurveyResponseId(null);
            }
          }
        }

        if (!fetchedAllowMultiple) {
          const foundSrid = loadSurveyResponseId();
          if (foundSrid) {
            const paramsWithSrid = new URLSearchParams();
            paramsWithSrid.set("surveyUrl", modifiedSurveyUrlName);
            paramsWithSrid.set("lang", languageRequest.language);
            paramsWithSrid.set("surveyResponseId", foundSrid);
            const urlWithSrid = `/api/survey-public/find-survey?${paramsWithSrid.toString()}`;
            const resWithSrid = await fetch(urlWithSrid, {
              headers: { "Content-Type": "application/json" },
              credentials: "omit",
              cache: "no-store",
            });
            if (!resWithSrid.ok) {
              const errorText = await resWithSrid.text();
              console.error(
                "API Error:",
                resWithSrid.status,
                resWithSrid.statusText,
                errorText
              );
              throw new Error(
                `API Error: ${resWithSrid.status} ${resWithSrid.statusText}`
              );
            }
            surveyData = (await resWithSrid.json()) as unknown as PublicSurvey;
          }
        }

        if (cancelled) return;
        if (!getCompletedAtFromSurveyData(surveyData) && (surveyData.allQuestions || surveyData.questions || []).some(
          (question) => getMomentusOptionsConfig(question)?.enabled || question.momentusOptions,
        )) {
          const id = String(surveyData.survey.id || surveyData.survey._id);
          const selectedResponseId = (surveyData as Partial<MomentusSurveyPayload>).surveyResponseId || undefined;
          // Strict Mode and translation refreshes can overlap the initial load.
          // Share creation until its ID is persisted; the server pins each response atomically.
          const pending = momentusInitializationRef.current?.surveyId === id ? momentusInitializationRef.current : null;
          const initialize = pending?.promise || momentusOptionsMutation.mutateAsync({
            surveyId: id,
            action: "initialize_momentus_options",
            surveyResponseId: selectedResponseId,
            responseLanguage: languageRequest.language,
            trackedLink: getTrackedLinkSlug() || undefined,
          });
          momentusInitializationRef.current = pending || { surveyId: id, language: languageRequest.language, promise: initialize };
          let initializationResolved = false;
          try {
            surveyData = await initialize;
            initializationResolved = true;
            if (cancelled) return;
            if (pending && pending.language !== languageRequest.language) {
              surveyData = await momentusOptionsMutation.mutateAsync({
                surveyId: id, action: "initialize_momentus_options",
                surveyResponseId: surveyData.surveyResponseId,
                responseLanguage: languageRequest.language,
              });
              if (cancelled) return;
            }
            const initialized = surveyData as MomentusSurveyPayload;
            surveyResponseIdRef.current = initialized.surveyResponseId;
            setSurveyResponseId(initialized.surveyResponseId);
            persistSurveyResponseId(initialized.surveyResponseId);
          } finally {
            // A cancelled load must leave its successful result for the next load
            // until that load persists the response ID.
            if ((!cancelled || !initializationResolved) && momentusInitializationRef.current?.promise === initialize) momentusInitializationRef.current = null;
          }
        }

        setSurvey(surveyData);
        if (surveyData.language?.enabled) {
          setSelectedLanguage(surveyData.language.selectedLanguage);
          persistSurveyLanguagePreference(surveyData.language.selectedLanguage);
          const effectiveLanguage = surveyData.language.effectiveLanguage;
          const previousEffectiveLanguage = previousEffectiveLanguageRef.current;
          if (
            previousEffectiveLanguage &&
            previousEffectiveLanguage !== effectiveLanguage &&
            effectiveLanguage !== SURVEY_BASE_LANGUAGE &&
            effectiveLanguage === surveyData.language.selectedLanguage
          ) {
            const definition = SURVEY_LANGUAGE_BY_CODE[effectiveLanguage];
            toast.info(
              PUBLIC_SURVEY_UI_COPY[effectiveLanguage].translationReady(
                definition?.englishName ?? effectiveLanguage,
              ),
            );
          }
          previousEffectiveLanguageRef.current = effectiveLanguage;
          if (
            languageRequest.source === "browser" &&
            languageRequest.language !== SURVEY_BASE_LANGUAGE &&
            surveyData.language.selectedLanguage === languageRequest.language
          ) {
            const trackKey = `${surveyData.survey?.id || surveyData.survey?._id || "survey"}:${languageRequest.language}`;
            if (languageAutoSelectionTrackedRef.current !== trackKey) {
              languageAutoSelectionTrackedRef.current = trackKey;
              trackPublicSurveyActivity(
                "survey.public.language_auto_selected",
                {
                  selectedLanguage: languageRequest.language,
                  effectiveLanguage,
                  fallbackReason: surveyData.language.fallbackReason ?? null,
                },
                `Auto-selected ${SURVEY_LANGUAGE_BY_CODE[languageRequest.language].englishName}`,
              );
            }
          }
        } else {
          previousEffectiveLanguageRef.current = SURVEY_BASE_LANGUAGE;
        }

        const hydratedData = surveyData as PublicSurvey & {
          surveyResponseId?: string;
          name?: string;
          email?: string;
        };
        const hydratedSurveyResponseId =
          typeof hydratedData.surveyResponseId === "string"
            ? hydratedData.surveyResponseId
            : null;
        if (hydratedSurveyResponseId) {
          setSurveyResponseId(hydratedSurveyResponseId);
          persistSurveyResponseId(hydratedSurveyResponseId);
          const hydratedIsAnonymous = surveyData.survey?.isAnonymous === true;
          if (!hydratedIsAnonymous) {
            const savedName = typeof hydratedData.name === "string" ? hydratedData.name : null;
            const savedEmail = typeof hydratedData.email === "string" ? hydratedData.email : null;
            if (savedName) setName(savedName);
            if (savedEmail) setEmail(normalizeEmailAddress(savedEmail));
            if (savedName && savedEmail) setContactInfoSubmitted(true);
          }
        }

        try {
          const completedAtCandidate = getCompletedAtFromSurveyData(surveyData);
          if (completedAtCandidate) {
            if (fetchedAllowMultiple) {
              clearPersistedSurveyResponseId();
              setSurveyResponseId(null);
              setContactInfoSubmitted(false);
            } else {
              setIsSubmitted(true);
            }
          } else if (fetchedAllowMultiple) {
            setIsSubmitted(false);
          }
        } catch {}

        // Pre-populate answers if available (value on questions)
        const prefilled: Record<string, AnswerValue> = {};
        (surveyData.allQuestions || surveyData.questions || []).forEach((q) => {
          const anyQ = q as unknown as { value?: unknown };
          if (typeof anyQ?.value !== "undefined" && anyQ.value !== null) {
            prefilled[q.id] = normalizePrefilledValue(
              q as SurveyQuestion,
              anyQ.value
            );
          }
        });
        if (Object.keys(prefilled).length > 0) {
          answersRef.current = prefilled;
          setAnswers(prefilled);
        }
        if (fetchedAllowMultiple && Object.keys(prefilled).length === 0) {
          answersRef.current = {};
          setAnswers({});
        }

        // Start fade out transition
        setIsLoading(false);

        // Small delay to ensure smooth transition
        setTimeout(() => {
          setShowContent(true);
        }, 50);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to fetch survey:", error);
        setError(
          error instanceof Error ? error.message : "Failed to fetch survey"
        );
        setIsLoading(false);
        // You might want to set an error state here
      }
    };
    fetchSurvey();
    return () => { cancelled = true; };
  }, [surveyUrlName, organizationName, fetchRetry, languageRefreshNonce]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isLoading || survey || error) return;
    const timeout = window.setTimeout(() => {
      if (isLoadingRef.current && !surveyRef.current) {
        setFetchRetry((value) => value + 1);
      }
    }, 5_000);
    return () => window.clearTimeout(timeout);
  }, [error, isLoading, survey]);

  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      if (isLoadingRef.current || !surveyRef.current) {
        window.location.reload();
        return;
      }
      setIsLoading(false);
      setShowContent(true);
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      window.setTimeout(() => {
        if (isLoadingRef.current) window.location.reload();
      }, 500);
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    // Wait until the survey definition is loaded before seeding the
    // surveyResponseId state — otherwise we may capture a URL/cookie value
    // before we know whether the survey is repeatable, which would leak a
    // prior response into a brand-new visit.
    if (!survey) return;
    const srid = loadSurveyResponseId();
    if (srid && srid !== surveyResponseId) setSurveyResponseId(srid);
  }, [survey, loadSurveyResponseId, surveyResponseId]);

  useEffect(() => {
    const surveyId = survey?.survey?.id || survey?.survey?._id;
    if (!surveyId || typeof window === "undefined") return;

    const scanId = getQueryParam("scanId");
    const trackedLink = getTrackedLinkSlug();
    const source = trackedLink ? "tracked-link" : getQueryParam("source") || (scanId ? "qr" : "direct");
    const sourceDetail = trackedLink || getQueryParam("sourceDetail");
    const trackKey = `${surveyId}:${scanId || ""}:${window.location.href}`;
    if (trackedViewRef.current === trackKey) return;
    trackedViewRef.current = trackKey;

    const visitorId = getOrCreateSurveyVisitorId();
    void fetch(`/api/survey-public/${surveyId}/track-view`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "omit",
      body: JSON.stringify({
        visitorId,
        scanId,
        source,
        sourceDetail,
        surveyResponseId: loadSurveyResponseId() || undefined,
        referer: document.referrer || undefined,
      }),
    }).catch((error) => {
      console.warn("Failed to track survey view", error);
    });
  }, [survey?.survey?.id, survey?.survey?._id, loadSurveyResponseId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const surveyId = getSurveyId();
    if (!surveyId || typeof window === "undefined") return;

    const route = sanitizePublicSurveyPath(window.location.pathname);
    const pageViewKey = `${surveyId}:${window.location.href}`;
    activeViewRef.current = { route, startedAt: Date.now() };
    if (publicSurveyActivityPageViewRef.current !== pageViewKey) {
      publicSurveyActivityPageViewRef.current = pageViewKey;
      trackPublicSurveyActivity(
        "survey.public.page_view",
        {
          route,
          source: getTrackedLinkSlug()
            ? "tracked-link"
            : getQueryParam("source") || (getQueryParam("scanId") ? "qr" : "direct"),
          trackedLinkPresent: Boolean(getTrackedLinkSlug()),
          scanIdPresent: Boolean(getQueryParam("scanId")),
          hasSurveyResponseId: Boolean(loadSurveyResponseId() || surveyResponseIdRef.current),
        },
        "Viewed public survey",
      );
      void flushPublicSurveyActivity();
    }

    return () => {
      endActivePublicSurveyView("route_change");
    };
  }, [
    getSurveyId,
    trackPublicSurveyActivity,
    flushPublicSurveyActivity,
    endActivePublicSurveyView,
    loadSurveyResponseId,
    getTrackedLinkSlug,
    getQueryParam,
  ]);

  useEffect(() => {
    const surveyId = getSurveyId();
    if (!surveyId || typeof window === "undefined") return;

    const interval = window.setInterval(() => {
      void flushPublicSurveyActivity();
    }, PUBLIC_SURVEY_ACTIVITY_FLUSH_INTERVAL_MS);

    const onClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const element = target?.closest<HTMLElement>(
        "button,a,[role='button'],[role='menuitem'],[role='tab'],[data-clickable],[data-activity-label],input,label,textarea,select",
      );
      if (!element) return;

      const label = getPublicSurveyElementLabel(element);
      if (!label) return;

      const input = element instanceof HTMLInputElement ? element : element.querySelector<HTMLInputElement>("input");
      const controlType =
        input?.type ||
        (element instanceof HTMLTextAreaElement ? "textarea" : null) ||
        (element instanceof HTMLSelectElement ? "select" : null);

      trackPublicSurveyActivity(
        "survey.public.click",
        {
          label,
          elementRole: element.getAttribute("role") || element.tagName.toLowerCase(),
          controlType,
          href: element instanceof HTMLAnchorElement ? sanitizePublicSurveyPath(element.pathname) : undefined,
        },
        `Clicked ${label}`,
      );
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        endActivePublicSurveyView("hidden");
        void flushPublicSurveyActivity(true);
        return;
      }
      activeViewRef.current = {
        route: sanitizePublicSurveyPath(window.location.pathname),
        startedAt: Date.now(),
      };
    };

    const onPageHide = () => {
      endActivePublicSurveyView("pagehide");
      void flushPublicSurveyActivity(true);
    };

    document.addEventListener("click", onClick, { capture: true });
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.clearInterval(interval);
      document.removeEventListener("click", onClick, { capture: true });
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("pagehide", onPageHide);
    };
  }, [getSurveyId, trackPublicSurveyActivity, flushPublicSurveyActivity, endActivePublicSurveyView]);

  useEffect(() => {
    if (!survey?.language?.enabled) return;
    if (survey.language.selectedLanguage === SURVEY_BASE_LANGUAGE) return;
    if (!survey.language.fallbackReason) return;
    const timeout = window.setTimeout(() => {
      setLanguageRefreshNonce((nonce) => nonce + 1);
    }, SURVEY_TRANSLATION_POLL_INTERVAL_MS);
    return () => window.clearTimeout(timeout);
  }, [
    survey?.language?.enabled,
    survey?.language?.selectedLanguage,
    survey?.language?.fallbackReason,
  ]);

  const updateSurveyLanguageUrl = useCallback((language: SurveyLanguageCode) => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    url.searchParams.set("lang", language);
    window.history.replaceState(
      window.history.state,
      "",
      `${url.pathname}${url.search}${url.hash}`,
    );
  }, []);

  const selectSurveyLanguage = useCallback((nextLanguage: SurveyLanguageCode) => {
    const previousLanguage = selectedSurveyLanguage;
    setSelectedLanguage(nextLanguage);
    persistSurveyLanguagePreference(nextLanguage);
    updateSurveyLanguageUrl(nextLanguage);
    trackPublicSurveyActivity(
      "survey.public.language_changed",
      {
        previousLanguage,
        selectedLanguage: nextLanguage,
        previousEffectiveLanguage: survey?.language?.effectiveLanguage ?? SURVEY_BASE_LANGUAGE,
        fallbackReason: survey?.language?.fallbackReason ?? null,
      },
      `Changed survey language to ${SURVEY_LANGUAGE_BY_CODE[nextLanguage].englishName}`,
    );
    toast.info(
      nextLanguage === SURVEY_BASE_LANGUAGE
        ? "Showing English."
        : `Switching to ${SURVEY_LANGUAGE_BY_CODE[nextLanguage].englishName}.`,
    );
    if (languageMenuRef.current) languageMenuRef.current.open = false;
    setLanguageRefreshNonce((nonce) => nonce + 1);
  }, [
    persistSurveyLanguagePreference,
    selectedSurveyLanguage,
    survey?.language?.effectiveLanguage,
    survey?.language?.fallbackReason,
    trackPublicSurveyActivity,
    updateSurveyLanguageUrl,
  ]);

  const brandColor = useMemo(
    () => getBrandColor(survey?.survey, survey?.organization),
    [survey],
  );

  const logoUrl = useMemo(
    () => getLogoUrl(survey?.survey, survey?.organization),
    [survey],
  );

  // Theme, background style, and `--sl-brand` come from the shared
  // survey-renderer module so the editor preview and this page stay
  // pixel-identical when appearance settings change.
  const surveyThemeData = useMemo(
    () => getSurveyTheme(survey?.survey, survey?.organization),
    [survey],
  );
  const appearanceTheme = surveyThemeData.dataTheme;
  const cardStyle = surveyThemeData.cardStyle;
  const verticalAlignment = surveyThemeData.verticalAlignment;

  // The root layout sets <html data-color-mode> from the visitor's cached
  // platform preference. Public surveys should reflect the survey's own
  // appearance instead, so override while mounted and restore on unmount.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const html = document.documentElement;
    const previous = html.getAttribute("data-color-mode");
    html.setAttribute("data-color-mode", appearanceTheme);
    return () => {
      if (previous === null) {
        html.removeAttribute("data-color-mode");
      } else {
        html.setAttribute("data-color-mode", previous);
      }
    };
  }, [appearanceTheme]);

  const SortedQuestions: SurveyQuestion[] = useMemo(() => {
    if (!survey?.allQuestions) return [] as SurveyQuestion[];
    return [...survey.allQuestions].sort(
      (a, b) => parseInt(a.orderId) - parseInt(b.orderId)
    );
  }, [survey]);

  // Authored content for the post-submit "Survey Completed" screen.
  // Sorted by `orderId` so the order set in the editor's completion
  // section is preserved here. Empty when the survey has no authored
  // completion blocks; the submitted step then falls back to the
  // legacy hardcoded copy.
  const completionBlocks: SurveyQuestion[] = useMemo(() => {
    const list = survey?.completionBlocks;
    if (!Array.isArray(list)) return [] as SurveyQuestion[];
    return [...list]
      .filter((question) =>
        evaluateShouldRenderQuestion(question, answers as Record<string, unknown>)
      )
      .sort(
        (a, b) => parseInt(a.orderId) - parseInt(b.orderId)
      );
  }, [survey?.completionBlocks, answers]);

  // Pagination by page field. We treat the survey's "pages" as the sorted
  // set of *distinct* page numbers actually used by questions, not the
  // numeric range 1..max. This means non-contiguous values (e.g. only
  // pages 1 and 3 exist) and gaps from deleted pages don't surface as
  // empty steps for respondents. `currentPage` is an index into that
  // sorted distinct list; `pageDirection` drives the slide animation
  // direction between pages.
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageDirection, setPageDirection] = useState<1 | -1>(1);
  const visibleFlowQuestions = useMemo(() => {
    return SortedQuestions.filter(
      (question) =>
        question.pageRole !== "completion" &&
        evaluateShouldRenderQuestion(question, answers as Record<string, unknown>)
    );
  }, [SortedQuestions, answers]);

  useEffect(() => {
    const visibleQuestionIds = new Set(visibleFlowQuestions.map((question) => question.id));
    setValidationErrors((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([questionId]) => visibleQuestionIds.has(questionId))
      );
      return Object.keys(next).length === Object.keys(prev).length ? prev : next;
    });
  }, [visibleFlowQuestions]);

  // Shared with the edit editor so a gap in `question.page`
  // collapses identically in both places.
  const { pages, totalPages: maxPage } = useSurveyPagination(visibleFlowQuestions);
  const currentPageQuestions = useMemo(() => {
    const idx = Math.max(0, currentPage - 1);
    return pages[idx]?.questions ?? [];
  }, [pages, currentPage]);
  useEffect(() => {
    setCurrentPage(1);
    setPageDirection(1);
  }, [survey?.allQuestions]);

  // If the set of pages shrinks (a page was removed), clamp the index.
  useEffect(() => {
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [currentPage, maxPage]);

  useEffect(() => {
    // the the page title to the survey name
    if (survey?.survey.name) {
      document.title = `${survey.organization?.name} | ${survey.survey.name}`;
    }
  }, [survey?.survey?.name, survey?.organization?.name]);

  // Normalize server-provided values to internal formats expected by inputs
  const normalizePrefilledValue = (
    question: SurveyQuestion,
    raw: unknown
  ): AnswerValue => {
    const typeKey = String(question.questionType).toLowerCase();
    const choices = Array.isArray(question.choices) ? question.choices : [];
    const byValue = new Map<string, string>();
    const byLabel = new Map<string, string>();
    for (const c of choices) {
      const valueKey = String(c.value);
      const labelKey = String(c.text).trim();
      byValue.set(valueKey, labelKey);
      byLabel.set(labelKey, valueKey);
    }

    // Matrix / ranking: support either compact map (opt_x=5;...) or label
    // summary (Label: 5, ...). Both store one numeric value per row.
    if (typeKey === "matrix" || typeKey === "ranking") {
      if (typeof raw !== "string" || !raw) return "";
      const str = String(raw);
      if (str.includes("=")) return str; // already in compact form
      // Attempt to reconstruct from label summary
      const parts = str.split(",");
      const segments: string[] = [];
      for (const p of parts) {
        const seg = p.trim();
        if (!seg) continue;
        const idx = seg.lastIndexOf(":");
        if (idx === -1) continue;
        const label = seg.slice(0, idx).trim();
        const rating = seg.slice(idx + 1).trim();
        const optValue = byLabel.get(label);
        if (!optValue) continue;
        segments.push(`${optValue}=${rating}`);
      }
      return segments.join(";");
    }

    // Single-select: prefer returning choice value (e.g. opt_2)
    if (
      typeKey === "single_select" ||
      typeKey === "single select" ||
      typeKey === "single-select"
    ) {
      if (raw == null) return "";
      const str = String(Array.isArray(raw) ? raw[0] : raw).trim();
      if (byValue.has(str)) return str;
      const mapped = byLabel.get(str);
      return mapped || str;
    }

    // Multi-select: return array of choice values
    if (
      typeKey === "multi_select" ||
      typeKey === "multi select" ||
      typeKey === "multi-select"
    ) {
      const toValues = (items: string[]): string[] => {
        const out: string[] = [];
        for (const it of items) {
          const s = String(it).trim();
          if (!s) continue;
          if (byValue.has(s)) {
            out.push(s);
          } else if (byLabel.has(s)) {
            out.push(byLabel.get(s) as string);
          } else if (isOtherAnswer(s)) {
            out.push(s);
          }
        }
        return out;
      };

      if (Array.isArray(raw)) {
        return toValues(raw.map(String));
      }
      if (typeof raw === "string") {
        // Could be comma-separated labels/values
        const items = raw.split(",").map((s) => s.trim());
        return toValues(items);
      }
      return [] as string[];
    }

    // Default: pass through
    return raw as AnswerValue;
  };

  const areAnswerValuesEqual = (
    a: AnswerValue | undefined,
    b: AnswerValue | undefined
  ): boolean => {
    const aIsArray = Array.isArray(a);
    const bIsArray = Array.isArray(b);
    if (aIsArray || bIsArray) {
      const aa = (Array.isArray(a) ? a : []) as string[];
      const bb = (Array.isArray(b) ? b : []) as string[];
      if (aa.length !== bb.length) return false;
      for (let i = 0; i < aa.length; i++) {
        if (aa[i] !== bb[i]) return false;
      }
      return true;
    }
    return a === b;
  };

  const getAnswerableQuestions = () =>
    (survey?.allQuestions || []).filter(
      (question) => !question.isContentOnly && question.pageRole !== "completion"
    );

  const pruneHiddenAnswers = (answersSnapshot: Record<string, AnswerValue>) => {
    const answerableQuestions = getAnswerableQuestions();
    const knownQuestionIds = new Set(answerableQuestions.map((question) => question.id));
    const visibleQuestionIds = new Set(
      answerableQuestions
        .filter((question) =>
          evaluateShouldRenderQuestion(question, answersSnapshot as Record<string, unknown>)
        )
        .map((question) => question.id)
    );
    const prunedAnswers: Record<string, AnswerValue> = {};
    const hiddenQuestionIds: string[] = [];

    for (const [questionId, value] of Object.entries(answersSnapshot)) {
      if (!knownQuestionIds.has(questionId) || visibleQuestionIds.has(questionId)) {
        prunedAnswers[questionId] = value;
      } else {
        hiddenQuestionIds.push(questionId);
      }
    }

    return { prunedAnswers, hiddenQuestionIds };
  };

  const getRequiredAnswerError = (
    question: SurveyQuestion,
    answersSnapshot: Record<string, AnswerValue>,
  ) => {
    if (!question.required || question.isContentOnly || question.pageRole === "completion") {
      return null;
    }

    const value = answersSnapshot[question.id];
    const typeKey = String(question.questionType).toLowerCase();
    const isEmptyScalar =
      value === undefined ||
      value === null ||
      (typeof value === "string" &&
        (value.trim().length === 0 || isBlankOtherAnswer(value)));

    if (typeKey === "file-upload") {
      if (typeof value !== "string" || !value.trim()) return surveyUiCopy.requiredQuestion;
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) && parsed.length > 0
          ? null
          : surveyUiCopy.requiredFile;
      } catch {
        return surveyUiCopy.requiredFile;
      }
    }

    if (typeKey === "matrix") {
      if (typeof value !== "string" || !value.trim()) return surveyUiCopy.requiredMatrix;
      const answeredRows = compactRowsAnswered(value);
      const rows = Array.isArray(question.choices) ? question.choices : [];
      if (rows.length === 0) return answeredRows.size > 0 ? null : surveyUiCopy.requiredMatrix;
      return rows.every((row) => answeredRows.has(String(row.value)))
        ? null
        : surveyUiCopy.requiredMatrixEachRow;
    }

    if (typeKey === "ranking") {
      if (typeof value !== "string" || !value.trim()) return surveyUiCopy.requiredQuestion;
      const answeredRows = compactRowsAnswered(value, { requireOtherText: true });
      const rows = Array.isArray(question.choices) ? question.choices : [];
      const configuredMax = Number(
        (question as { ranking?: { maxRank?: unknown } }).ranking?.maxRank,
      );
      const maxRank =
        Number.isFinite(configuredMax) && configuredMax > 0
          ? Math.floor(configuredMax)
          : DEFAULT_MAX_RANK;
      // Respondents only assign as many ranks as there are ranks or items,
      // whichever is fewer (a "top N" ranking when items outnumber ranks).
      const requiredCount = Math.min(maxRank, rows.length);
      if (requiredCount === 0) return answeredRows.size > 0 ? null : surveyUiCopy.requiredQuestion;
      return answeredRows.size >= requiredCount
        ? null
        : surveyUiCopy.requiredMatrixEachRow;
    }

    if (Array.isArray(value)) {
      const nonBlankValues = value.filter((item) => !isBlankOtherAnswer(item));
      return nonBlankValues.length > 0 ? null : surveyUiCopy.requiredQuestion;
    }
    if (typeof value === "boolean") return null;
    if (typeof value === "number") return Number.isFinite(value) ? null : surveyUiCopy.requiredQuestion;
    return isEmptyScalar ? surveyUiCopy.requiredQuestion : null;
  };

  const validateVisibleQuestions = (questions: SurveyQuestion[]) => {
    const nextErrors: Record<string, string> = {};
    for (const question of questions) {
      const errorMessage = getRequiredAnswerError(question, answersRef.current);
      if (errorMessage) nextErrors[question.id] = errorMessage;
    }
    setValidationErrors((prev) => {
      const scopedIds = new Set(questions.map((question) => question.id));
      const retained = Object.fromEntries(
        Object.entries(prev).filter(([questionId]) => !scopedIds.has(questionId))
      );
      return { ...retained, ...nextErrors };
    });
    return Object.keys(nextErrors).length === 0;
  };

  const submitAnswers = async (opts?: {
    isComplete?: boolean;
    includeContact?: boolean;
    answersOverride?: Record<string, AnswerValue>;
  }) => {
    if (!survey) return null;
    const runSubmission = async () => {
      let answersSnapshot: Record<string, AnswerValue> = {
        ...(opts?.answersOverride || answersRef.current),
      };
      const { prunedAnswers, hiddenQuestionIds } = pruneHiddenAnswers(answersSnapshot);
      answersSnapshot = prunedAnswers;
      if (hiddenQuestionIds.length > 0) {
        for (const questionId of hiddenQuestionIds) {
          pendingHiddenQuestionIdsRef.current.add(questionId);
        }
        answersRef.current = prunedAnswers;
        setAnswers(prunedAnswers);
      }

      // Parse the renderer's compact in-memory matrix value
      // ("opt_1=4;opt_2=N/A") into a structured per-cell payload that the
      // backend persists as one SurveyQuestionResponse per cell. Snapshots
      // the row + column labels so renaming a row after submission doesn't
      // rewrite history.
      type MatrixCellPayload = {
        rowValue: string;
        rowText: string;
        columnValue: number | string;
        columnText: string;
      };
      const buildMatrixCells = (
        question: SurveyQuestion,
        raw: AnswerValue,
      ): MatrixCellPayload[] => {
        const str = String(raw || "");
        if (!str) return [];
        const valueByKey = parseCompactAnswerMap(str);
        const typeKey = String(question.questionType).toLowerCase();
        const otherText = decodeOtherTextValue(valueByKey[SELECT_OTHER_TEXT_KEY]);
        const choices = Array.isArray(question.choices) ? question.choices : [];
        const rowByValue = new Map<string, string>();
        for (const c of choices) {
          rowByValue.set(String(c.value), String(c.text).trim());
        }
        const matrixConfig =
          (question as { matrix?: { columns?: Array<{ value: unknown; text?: unknown }> } }).matrix;
        const columnByValue = new Map<string, string>();
        for (const col of matrixConfig?.columns ?? []) {
          const colValue = String(col?.value ?? "").trim();
          const colText = String(col?.text ?? "").trim();
          if (colValue) columnByValue.set(colValue, colText);
        }

        const cells: MatrixCellPayload[] = [];
        for (const [rowValue, rawColumn] of Object.entries(valueByKey)) {
          if (rowValue === SELECT_OTHER_TEXT_KEY) continue;
          const ratingText = String(rawColumn).trim();
          if (!ratingText) continue;
          const isRankingOther =
            typeKey === "ranking" && rowValue === SELECT_OTHER_VALUE;
          if (isRankingOther && !hasOtherAnswerText(otherText)) continue;
          const rowText = isRankingOther
            ? toOtherAnswer(otherText)
            : (rowByValue.get(rowValue) || rowValue).trim();
          if (ratingText.toUpperCase() === "N/A") {
            cells.push({
              rowValue,
              rowText,
              columnValue: "N/A",
              columnText: columnByValue.get("N/A") || "N/A",
            });
            continue;
          }
          const numeric = Number(ratingText);
          const columnText = Number.isFinite(numeric)
            ? columnByValue.get(String(numeric)) || ""
            : columnByValue.get(ratingText) || "";
          cells.push({
            rowValue,
            rowText,
            columnValue: Number.isFinite(numeric) ? numeric : ratingText,
            columnText,
          });
        }
        return cells;
      };

      // Submit select answers with stable choice values while snapshotting the
      // localized display label and the original English label for reporting.
      const formatSelectAnswer = (
        question: SurveyQuestion,
        raw: AnswerValue
      ): {
        value: string | string[];
        displayText: string;
        baseDisplayText: string;
      } => {
        const choices = Array.isArray(question.choices) ? question.choices : [];
        const byValue = new Map<string, {
          displayText: string;
          baseDisplayText: string;
        }>();
        for (const c of choices) {
          byValue.set(String(c.value), {
            displayText: String(c.displayText || c.text || c.value).trim(),
            baseDisplayText: String(c.baseDisplayText || c.text || c.value).trim(),
          });
        }
        if (Array.isArray(raw)) {
          const values: string[] = [];
          const displayLabels: string[] = [];
          const baseLabels: string[] = [];
          for (const v of raw) {
            if (isBlankOtherAnswer(v)) continue;
            const key = String(v);
            const labels = byValue.get(key);
            values.push(key);
            displayLabels.push((labels?.displayText || key).trim());
            baseLabels.push((labels?.baseDisplayText || key).trim());
          }
          return {
            value: values,
            displayText: displayLabels.join(", "),
            baseDisplayText: baseLabels.join(", "),
          };
        }
        if (typeof raw === "string") {
          if (isBlankOtherAnswer(raw) || !raw) {
            return { value: "", displayText: "", baseDisplayText: "" };
          }
          const labels = byValue.get(raw);
          return {
            value: raw,
            displayText: (labels?.displayText || raw).trim(),
            baseDisplayText: (labels?.baseDisplayText || raw).trim(),
          };
        }
        return { value: "", displayText: "", baseDisplayText: "" };
      };

      const surveyId = getSurveyId();
      if (!surveyId) return null;

      let requestSurveyResponseId = allowMultipleSubmissions
        ? surveyResponseIdRef.current
        : loadSurveyResponseId() || surveyResponseIdRef.current;
      if (!requestSurveyResponseId && responseCreationPromiseRef.current) {
        requestSurveyResponseId = await responseCreationPromiseRef.current;
      }
      const hiddenQuestionIdsForRequest = Array.from(new Set([
        ...pendingHiddenQuestionIdsRef.current,
        ...hiddenQuestionIds,
      ]));

      const requestBody = {
        surveyResponseId: requestSurveyResponseId || undefined,
        responseLanguage: selectedSurveyLanguage,
        isComplete: Boolean(opts?.isComplete),
        // Only attach contact info for non-anonymous surveys
        ...(opts?.includeContact && !isAnonymousSurvey && name && email
          ? { name, email }
          : {}),
        hiddenQuestionIds: hiddenQuestionIdsForRequest,
        trackedLink: getTrackedLinkSlug() || undefined,
        answers: Object.entries(answersSnapshot)
          .map(([questionId, value]) => {
            const question = survey?.allQuestions.find(
              (q) => q.id === questionId
            );
            if (!question) return null;
            // Skip content-only blocks — they don't produce answers
            if (question.isContentOnly) return null;
            const typeKey = String(question.questionType).toLowerCase();
            // Matrix and ranking both get a structured cells[] payload —
            // the backend writes one SurveyQuestionResponse per cell/item so
            // per-row analytics work out of the box. Ranking stores the
            // assigned rank as each cell's column value.
            if (typeKey === "matrix" || typeKey === "ranking") {
              const cells = buildMatrixCells(question, value);
              return {
                question: questionId,
                questionType: typeKey,
                cells,
                name: question.name,
                text: question.text || "",
                baseDisplayText: question.baseDisplayText || question.text || "",
                displayText: question.displayText || question.text || "",
                responseLanguage: selectedSurveyLanguage,
              };
            }
            let sentValue: AnswerValue = value;
            let displayText: string | undefined;
            let baseDisplayText: string | undefined;
            if (
              typeKey === "single_select" ||
              typeKey === "single select" ||
              typeKey === "single-select"
            ) {
              const formatted = formatSelectAnswer(question, value);
              sentValue = formatted.value as AnswerValue;
              displayText = formatted.displayText;
              baseDisplayText = formatted.baseDisplayText;
            } else if (
              typeKey === "multi_select" ||
              typeKey === "multi select" ||
              typeKey === "multi-select"
            ) {
              const formatted = formatSelectAnswer(question, value);
              sentValue = formatted.value as AnswerValue;
              displayText = formatted.displayText;
              baseDisplayText = formatted.baseDisplayText;
            } else {
              // For numeric answers, coerce numeric strings to numbers
              if (typeof sentValue === "string" && !isNaN(Number(sentValue))) {
                sentValue = Number(sentValue);
              }
            }
            // Always keep the original question prompt in 'text'
            const textValue = question.text || "";
            return {
              question: questionId,
              value: sentValue,
              name: question.name,
              text: textValue,
              displayText,
              baseDisplayText,
              responseLanguage: selectedSurveyLanguage,
            };
          })
          .filter(Boolean),
      };

      const sendRequest = async () => {
        const response = await fetch(`/api/survey-public/${surveyId}`, {
          method: "POST",
          body: JSON.stringify(requestBody),
          headers: { "Content-Type": "application/json" },
          credentials: "omit",
        });
        const bodyText = await response.text();
        let bodyData: Record<string, unknown> | null = null;
        try {
          bodyData = bodyText ? JSON.parse(bodyText) : null;
        } catch {}
        return { response, bodyText, bodyData };
      };

      const isCreatingResponse = !requestSurveyResponseId;
      const responsePromise = sendRequest();
      if (isCreatingResponse) {
        responseCreationPromiseRef.current = responsePromise
          .then(({ response, bodyData }) => {
            if (!response.ok) return null;
            const createdId = bodyData?.surveyResponseId;
            return typeof createdId === "string" ? createdId : null;
          })
          .finally(() => {
            responseCreationPromiseRef.current = null;
          });
      }

      const { response: res, bodyText: details, bodyData: data } = await responsePromise;
      if (!res.ok) {
        trackPublicSurveyActivity(
          "survey.public.error",
          {
            action: opts?.isComplete ? "submit_answers_complete" : "submit_answers_partial",
            status: res.status,
          },
          "Public survey answer save failed",
        );
        console.error("Failed to submit answers", res, details);
        setError(details || "Failed to submit answers");
        return null;
      }
      if (data?.surveyResponseId && typeof data.surveyResponseId === "string") {
        surveyResponseIdRef.current = data.surveyResponseId;
        setSurveyResponseId(data.surveyResponseId);
        if (allowMultipleSubmissions && opts?.isComplete) {
          clearPersistedSurveyResponseId();
        } else {
          persistSurveyResponseId(data.surveyResponseId);
        }
      }
      if (opts?.isComplete) {
        for (const questionId of hiddenQuestionIdsForRequest) {
          pendingHiddenQuestionIdsRef.current.delete(questionId);
        }
      }
      trackPublicSurveyActivity(
        "survey.public.response_saved",
        {
          isComplete: Boolean(opts?.isComplete),
          answerCount: Object.keys(answersSnapshot).length,
          includeContact: Boolean(opts?.includeContact && !isAnonymousSurvey),
          hasSurveyResponseId: Boolean(data?.surveyResponseId || surveyResponseIdRef.current),
          responseLanguage: selectedSurveyLanguage,
        },
        opts?.isComplete ? "Submitted public survey" : "Saved public survey response",
      );
      void flushPublicSurveyActivity();
      return data;
    };

    const nextSubmission = submitQueueRef.current.then(runSubmission, runSubmission);
    submitQueueRef.current = nextSubmission.then(
      () => undefined,
      () => undefined,
    );
    return nextSubmission;
  };

  const handleAnswerChange = (
    question: SurveyQuestion,
    rawValue: string | string[],
  ) => {
    const typeKey = String(question.questionType).toLowerCase();
    const previousAnswers = answersRef.current;
    const prevForQuestion = previousAnswers[question.id];

    let nextForQuestion: AnswerValue;
    // Multi-select toggles values in an array
    if (
      typeKey === "multi_select" ||
      typeKey === "multi select" ||
      typeKey === "multi-select"
    ) {
      if (Array.isArray(rawValue)) {
        nextForQuestion = rawValue;
      } else {
        const previousArray = Array.isArray(prevForQuestion)
          ? (prevForQuestion as string[])
          : [];
        const exists = previousArray.includes(rawValue);
        nextForQuestion = exists
          ? previousArray.filter((v) => v !== rawValue)
          : [...previousArray, rawValue];
      }
      const maxSelections = getMultiSelectMaxSelections(question);
      if (
        maxSelections !== null &&
        Array.isArray(nextForQuestion) &&
        nextForQuestion.length > maxSelections
      ) {
        nextForQuestion = nextForQuestion.slice(0, maxSelections);
      }
    } else if (typeKey === "boolean") {
      // Boolean
      nextForQuestion = rawValue === "true";
    } else if (
      typeKey === "rating" ||
      typeKey === "rating-nps" ||
      typeKey === "rating word" ||
      typeKey === "rating-word"
    ) {
      // Numeric ratings
      const numeric = Number(rawValue);
      nextForQuestion = Number.isNaN(numeric) ? rawValue : numeric;
    } else {
      // Default: single value string
      nextForQuestion = rawValue;
    }

    const changed = !areAnswerValuesEqual(prevForQuestion, nextForQuestion);
    if (!changed) return;

    const { prunedAnswers: nextAnswers, hiddenQuestionIds } = pruneHiddenAnswers({
      ...previousAnswers,
      [question.id]: nextForQuestion,
    });
    answersRef.current = nextAnswers;
    setAnswers(nextAnswers);
    setValidationErrors((prev) => {
      const next = { ...prev };
      delete next[question.id];
      for (const hiddenQuestionId of hiddenQuestionIds) delete next[hiddenQuestionId];
      return next;
    });

    const answerArrayForActivity = Array.isArray(nextForQuestion)
      ? nextForQuestion.filter((item) => !isBlankOtherAnswer(item))
      : null;
    const isBlankOtherScalar =
      !answerArrayForActivity && isBlankOtherAnswer(nextForQuestion);
    trackPublicSurveyActivity(
      "survey.public.answer_changed",
      {
        questionName: question.name || null,
        questionType: question.questionType || null,
        orderId: question.orderId || null,
        isRequired: Boolean(question.required),
        hasAnswer: answerArrayForActivity
          ? answerArrayForActivity.length > 0
          : !isBlankOtherScalar &&
            nextForQuestion !== "" &&
            nextForQuestion !== null &&
            typeof nextForQuestion !== "undefined",
        answerKind: Array.isArray(nextForQuestion) ? "array" : typeof nextForQuestion,
        answerSize: answerArrayForActivity
          ? answerArrayForActivity.length
          : typeof nextForQuestion === "string"
            ? isBlankOtherScalar
              ? 0
              : nextForQuestion.length
            : null,
      },
      `Answered ${question.name || question.questionType || "question"}`,
    );
    // For inputs like text/textarea we only persist on blur, so this submission is for discrete inputs
    // TextQuestion calls this handler onBlur already
    void submitAnswers({ answersOverride: nextAnswers });
  };

  const handleSubmitReview = async () => {
    try {
      if (!validateVisibleQuestions(visibleFlowQuestions)) {
        return;
      }
      setSending(true);
      setError(null);
      const result = await submitAnswers({
        isComplete: true,
        includeContact: true,
      });
      if (result) {
        trackPublicSurveyActivity(
          "survey.public.submitted",
          {
            answerCount: Object.keys(answers).length,
            linkedToReviewSite: Boolean(survey?.survey?.linkToReviewSite),
          },
          "Completed public survey",
        );
        void flushPublicSurveyActivity();
        setIsSubmitted(true);
        // Copy textarea answer if exists
        const textQuestion = survey?.allQuestions.find((q) => {
          const t = String(q.questionType).toLowerCase();
          return t === "textarea" || t === "text";
        });
        if (textQuestion) {
          const value = answers[textQuestion.id];
          if (typeof value === "string" && value.trim().length > 0) {
            try {
              await navigator.clipboard.writeText(value);
            } catch {}
          }
        }
        // Only persist contact cookies for non-anonymous surveys
        if (!isAnonymousSurvey) {
          if (name) setCookie("surveyUserName", name);
          if (email) setCookie("surveyUserEmail", normalizeEmailAddress(email));
        }
      }
    } catch (e) {
      trackPublicSurveyActivity(
        "survey.public.error",
        { action: "submit_review" },
        "Public survey submit failed",
      );
      setError(e instanceof Error ? e.message : "Failed to submit review");
    } finally {
      setSending(false);
    }
  };

  const submitContactInfo = async () => {
    if (contactSubmitInFlightRef.current) return;
    const validationError = validateEmail(email);
    setEmailError(validationError);
    setEmailTouched(true);
    if (!name.trim() || validationError || !survey) return;
    // Do not submit contact info for anonymous surveys
    if (isAnonymousSurvey) return;
    contactSubmitInFlightRef.current = true;
    setError(null);
    setSubmittingContactInfo(true);
    try {
      const surveyId = getSurveyId();
      if (!surveyId) throw new Error("Survey ID missing");
      let requestSurveyResponseId = allowMultipleSubmissions
        ? surveyResponseIdRef.current
        : loadSurveyResponseId() || surveyResponseIdRef.current;
      if (!requestSurveyResponseId && responseCreationPromiseRef.current) {
        requestSurveyResponseId = await responseCreationPromiseRef.current;
      }
      const payloadRes = await fetch(
        `/api/survey-public/${surveyId}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "omit",
            body: JSON.stringify({
              surveyResponseId: requestSurveyResponseId || undefined,
              responseLanguage: selectedSurveyLanguage,
              trackedLink: getTrackedLinkSlug() || undefined,
              ...(!isAnonymousSurvey ? { name, email } : {}),
              answers: [],
            isComplete: false,
          }),
        }
      );
      const bodyText = await payloadRes.text();
      let data: Record<string, unknown> | null = null;
      try {
        data = bodyText ? JSON.parse(bodyText) : null;
      } catch {}
      if (!payloadRes.ok) {
        const message =
          typeof data?.error === "string"
            ? data.error
            : "Failed to submit contact info";
        throw new Error(message);
      }

      if (data?.surveyResponseId && typeof data.surveyResponseId === "string") {
        surveyResponseIdRef.current = data.surveyResponseId;
        setSurveyResponseId(data.surveyResponseId);
        persistSurveyResponseId(data.surveyResponseId);
      }
      if (!isAnonymousSurvey) {
        setCookie("surveyUserName", name);
        setCookie("surveyUserEmail", normalizeEmailAddress(email));
      }
      trackPublicSurveyActivity(
        "survey.public.contact_submitted",
        {
          hasName: Boolean(name.trim()),
          hasEmail: Boolean(email.trim()),
          hasSurveyResponseId: Boolean(data?.surveyResponseId || surveyResponseIdRef.current),
          responseLanguage: selectedSurveyLanguage,
        },
        "Submitted public survey contact info",
      );
      void flushPublicSurveyActivity();
      setContactInfoSubmitted(true);
    } catch (e) {
      trackPublicSurveyActivity(
        "survey.public.error",
        { action: "submit_contact_info" },
        "Public survey contact submit failed",
      );
      setError(
        e instanceof Error ? e.message : "Failed to submit contact info"
      );
      setContactInfoSubmitted(false);
    } finally {
      contactSubmitInFlightRef.current = false;
      setSubmittingContactInfo(false);
    }
  };

  const shouldDisplayReviewSiteResult = useMemo(() => {
    if (!isSubmitted || !survey?.survey?.linkToReviewSite) return false;
    const reviewSiteConditions = (survey?.survey?.reviewSiteConditions ||
      []) as unknown as Array<{
      var?: string;
      operator?: string;
      value?: unknown;
    }>;
    if (!reviewSiteConditions || reviewSiteConditions.length === 0) return true;
    return reviewSiteConditions.some((c) => {
      if (!c || !c.var) return false;
      const answerValue = answers[c.var as string];
      if (
        answerValue === undefined ||
        answerValue === null ||
        (Array.isArray(answerValue) && answerValue.length === 0) ||
        (!Array.isArray(answerValue) && answerValue === "")
      )
        return false;

      const operator = c.operator || "==";
      const conditionValue = c.value;

      if (Array.isArray(answerValue)) {
        const arr = (answerValue as unknown[]).map((v) => String(v));
        const condStr = String(conditionValue ?? "");
        if (operator === "==") return arr.includes(condStr);
        if (operator === "!=") return !arr.includes(condStr);
        return false;
      }

      const targetValueStr = String(answerValue);
      const conditionValueStr = String(conditionValue ?? "");
      switch (operator) {
        case "==":
          return targetValueStr === conditionValueStr;
        case "!=":
          return targetValueStr !== conditionValueStr;
        case "<":
          return parseFloat(targetValueStr) < parseFloat(conditionValueStr);
        case "<=":
          return parseFloat(targetValueStr) <= parseFloat(conditionValueStr);
        case ">":
          return parseFloat(targetValueStr) > parseFloat(conditionValueStr);
        case ">=":
          return parseFloat(targetValueStr) >= parseFloat(conditionValueStr);
        default:
          return false;
      }
    });
  }, [isSubmitted, survey, answers]);

  const navigateToGoogleReview = (url?: string | null) => {
    trackPublicSurveyActivity(
      "survey.public.review_link_clicked",
      { hasUrl: Boolean(url) },
      "Clicked public survey review link",
    );
    void flushPublicSurveyActivity();
    if (url) window.open(url, "_blank");
  };

  // URL prefill for rating/answer after survey loads
  useEffect(() => {
    if (!survey) return;
    const hasDraftResponse = Boolean(
      (survey as unknown as { surveyResponseId?: string | null }).surveyResponseId ||
        surveyResponseIdRef.current ||
        loadSurveyResponseId()
    );
    // Repeatable surveys start fresh only when there is no incomplete draft.
    if (allowMultipleSubmissions && !hasDraftResponse) {
      setIsSubmitted(false);
      answersRef.current = {};
      setAnswers({});
    }
    const ratingParam = getQueryParam("rating");
    const answerParam = getQueryParam("answer");
    const choiceParam = getQueryParam("choice");
    const qidParam = getQueryParam("qid");
    const matchesQuestionId = (question: SurveyQuestion) => {
      if (!qidParam) return false;
      const rawQuestion = question as SurveyQuestion & { _id?: string | null };
      return rawQuestion.id === qidParam || rawQuestion._id === qidParam;
    };
    const applyPrefillAnswers = (nextAnswers: Record<string, AnswerValue>) => {
      answersRef.current = nextAnswers;
      setAnswers(nextAnswers);
      if (!shouldShowContactFields || contactInfoSubmitted) {
        void submitAnswers({ answersOverride: nextAnswers });
      }
    };
    if (ratingParam) {
      const ratingQs = (survey?.allQuestions || []).filter((q) => {
        const t = String(q.questionType).toLowerCase();
        return (
          t === "rating" ||
          t === "rating-word" ||
          t === "rating word" ||
          t === "rating-nps"
        );
      });
      if (ratingQs.length > 0) {
        const first = qidParam
          ? ratingQs.find(matchesQuestionId)
          : ratingQs.sort(
              (a, b) => (parseInt(a.orderId) || 0) - (parseInt(b.orderId) || 0)
            )[0];
        const n = parseInt(ratingParam);
        const t = String(first?.questionType || "").toLowerCase();
        const ratingScale =
          (first?.options as { scale?: number | string } | null)?.scale === 10 ||
          (first?.options as { scale?: number | string } | null)?.scale === "10"
            ? 10
            : 5;
        const valid =
          (t === "rating" && n >= 1 && n <= ratingScale) ||
          ((t === "rating-word" || t === "rating word") && n >= 1 && n <= 5) ||
          (t === "rating-nps" && n >= 0 && n <= 10);
        if (first && valid) {
          applyPrefillAnswers({ ...answersRef.current, [first.id]: n });
        }
      }
    }
    if (answerParam && (answerParam === "Yes" || answerParam === "No")) {
      const radioQs = (survey?.allQuestions || []).filter((q) => {
        const t = String(q.questionType).toLowerCase();
        return t === "radio" || t === "boolean";
      });
      if (radioQs.length > 0) {
        const first = qidParam
          ? radioQs.find(matchesQuestionId)
          : radioQs.sort(
              (a, b) => (parseInt(a.orderId) || 0) - (parseInt(b.orderId) || 0)
            )[0];
        if (first) {
          applyPrefillAnswers({
            ...answersRef.current,
            [first.id]: answerParam === "Yes" ? "true" : "false",
          });
        }
      }
    }

    // Generic choice prefill for first or targeted question
    if (choiceParam) {
      const questions = survey?.allQuestions || [];
      let target = qidParam
        ? questions.find(matchesQuestionId)
        : undefined;

      const byOrder = (a: SurveyQuestion, b: SurveyQuestion) =>
        (parseInt(a.orderId) || 0) - (parseInt(b.orderId) || 0);

      // If no explicit qid, find the earliest question that accepts this choice
      if (!target) {
        target = questions
          .filter((q) => {
            const t = String(q.questionType).toLowerCase();
            if (
              t === "single_select" ||
              t === "single select" ||
              t === "single-select" ||
              t === "multi_select" ||
              t === "multi select" ||
              t === "multi-select"
            ) {
              return (q.choices || []).some(
                (c) => String(c.value) === String(choiceParam)
              );
            }
            if (t === "radio" || t === "boolean") {
              const lc = choiceParam.toLowerCase();
              const mapped = lc === "yes" ? "true" : lc === "no" ? "false" : lc;
              return mapped === "true" || mapped === "false";
            }
            return false;
          })
          .sort(byOrder)[0];
      }

      if (target) {
        const typeKey = String(target.questionType).toLowerCase();
        if (
          typeKey === "single_select" ||
          typeKey === "single select" ||
          typeKey === "single-select"
        ) {
          applyPrefillAnswers({
            ...answersRef.current,
            [target!.id]: String(choiceParam),
          });
        } else if (
          typeKey === "multi_select" ||
          typeKey === "multi select" ||
          typeKey === "multi-select"
        ) {
          const prevVals = Array.isArray(answersRef.current[target!.id])
            ? (answersRef.current[target!.id] as string[])
            : [];
          if (!prevVals.includes(String(choiceParam))) {
            applyPrefillAnswers({
              ...answersRef.current,
              [target!.id]: [...prevVals, String(choiceParam)],
            });
          }
        } else if (typeKey === "radio" || typeKey === "boolean") {
          const lc = choiceParam.toLowerCase();
          const mapped = lc === "yes" ? "true" : lc === "no" ? "false" : lc;
          if (mapped === "true" || mapped === "false") {
            applyPrefillAnswers({
              ...answersRef.current,
              [target!.id]: mapped === "true",
            });
          }
        }
      }
    }
  }, [survey]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSelected = (
    question: SurveyQuestion,
    candidate: string | number
  ): boolean => {
    const current = answers[question.id];
    const typeKey = String(question.questionType).toLowerCase();
    if (
      typeKey === "multi_select" ||
      typeKey === "multi select" ||
      typeKey === "multi-select"
    ) {
      return Array.isArray(current) && current.includes(String(candidate));
    }
    if (typeKey === "boolean") {
      return (
        typeof current === "boolean" && String(current) === String(candidate)
      );
    }
    return current === candidate || String(current) === String(candidate);
  };

  const retryMomentusOptions = async (questionId: string) => {
    const surveyId = getSurveyId();
    const responseId = surveyResponseIdRef.current;
    if (!surveyId || !responseId || momentusOptionsMutation.isPending) return;
    trackPublicSurveyActivity("survey.public.click", { action: "retry_momentus_options", questionId }, "Retried event options");
    try {
      const refreshed = await momentusOptionsMutation.mutateAsync({
        surveyId,
        action: "retry_momentus_options",
        surveyResponseId: responseId,
        questionId,
        responseLanguage: selectedSurveyLanguage,
      });
      const resolved = refreshed.allQuestions.find((question) => question.id === questionId);
      if (!resolved) return;
      const mergeOptions = (question: SurveyQuestion) => question.id === questionId ? {
        ...question, choices: resolved.choices, required: resolved.required,
        momentusOptions: resolved.momentusOptions,
        options: { ...question.options, allowOther: resolved.options?.allowOther },
      } : question;
      setSurvey((current) => current ? {
        ...current, questions: current.questions.map(mergeOptions), allQuestions: current.allQuestions.map(mergeOptions),
      } : current);
    } catch {
      toast.error("We couldn’t load events. Please try again.");
    }
  };

  const renderQuestion = (q: SurveyQuestion) => (
    <SurveyRenderer
      mode="live"
      question={q}
      uiStrings={surveyUiCopy}
      brandColor={brandColor}
      answer={answers[q.id]}
      isSelected={isSelected}
      onAnswerChange={handleAnswerChange}
      surveyId={getSurveyId() || undefined}
      onRetryMomentusOptions={retryMomentusOptions}
      momentusOptionsRetrying={momentusOptionsMutation.isPending}
    />
  );

  const submissionTitle =
    typeof survey?.survey?.submissionTitle === "string" &&
    survey.survey.submissionTitle.trim()
      ? survey.survey.submissionTitle.trim()
      : null;
  const submissionSharePrompt =
    typeof survey?.survey?.submissionSharePrompt === "string" &&
    survey.survey.submissionSharePrompt.trim()
      ? survey.survey.submissionSharePrompt.trim()
      : null;

  // Decide which "step" of the flow to render. Order:
  //   1. blocked → access denied
  //   2. submitted → thank-you screen
  //   3. contact → name/email gate (only when required + not yet submitted)
  //   4. questions → the paged question flow
  const accessBlocked = Boolean(survey && showContent && !isAccessAllowed);
  const showContactStep = Boolean(
    !isSubmitted && shouldShowContactFields && !contactInfoSubmitted
  );
  const stepKey: string = accessBlocked
    ? "blocked"
    : isSubmitted
      ? "submitted"
      : showContactStep
        ? "contact"
        : "questions";

  // Progress bar position. While on the contact gate we show 0%. While in
  // questions, scale by current page over total pages. On the submitted
  // screen we render 100%.
  const progressPercent = (() => {
    if (!survey || !showContent) return 0;
    if (stepKey === "submitted") return 100;
    if (stepKey !== "questions") return 0;
    if (maxPage <= 1) return 50;
    return Math.round((currentPage / maxPage) * 100);
  })();

  const goToNextPage = () => {
    if (!validateVisibleQuestions(currentPageQuestions)) return;
    setPageDirection(1);
    setCurrentPage((p) => Math.min(maxPage, p + 1));
    try {
      window.requestAnimationFrame(() =>
        window.scrollTo({ top: 0, behavior: "smooth" })
      );
    } catch {}
  };

  const goToPrevPage = () => {
    setPageDirection(-1);
    setCurrentPage((p) => Math.max(1, p - 1));
    try {
      window.requestAnimationFrame(() =>
        window.scrollTo({ top: 0, behavior: "smooth" })
      );
    } catch {}
  };

  const headerBlock = (
    <SurveyBrandHeader
      logoUrl={logoUrl}
      organization={survey?.organization ?? null}
      alignment={surveyThemeData.logoAlignment}
      size={surveyThemeData.logoSize}
    />
  );

  const footerBlock = (
    <SurveyBrandFooter organization={survey?.organization ?? null} />
  );

  return (
    <div
      className={`${styles.root} ${surveyTheme.surfaceTheme}`}
      data-theme={appearanceTheme}
      style={surveyThemeData.style}
    >
      <div
        className={styles.progressbar}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={progressPercent}
      >
        <div
          className={styles.progressbar__fill}
          style={{ width: `${progressPercent}%` }}
        />
      </div>

      <AnimatePresence mode="wait">
        {isLoading && (
          <motion.div
            key="loading"
            className={styles.loadingOverlay}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
          >
            <LoadingSpinner isVisible={isLoading} />
          </motion.div>
        )}
      </AnimatePresence>

      {!isLoading && !survey && error && (
        <main
          className={styles.shell}
          data-vertical-alignment={verticalAlignment}
        >
          <section className={styles.card} data-style={cardStyle}>
            <div className={styles.step} role="alert" aria-live="polite">
              <p className={styles.step__eyebrow}>{surveyUiCopy.surveyUnavailable}</p>
              <h1 className={styles.step__title}>{error}</h1>
              <p className={styles.step__lede}>
                {surveyUiCopy.accessMissingResponse}
              </p>
            </div>
          </section>
        </main>
      )}

      {survey && showContent && (
        <main
          className={styles.shell}
          data-vertical-alignment={verticalAlignment}
        >
          <motion.section
            className={styles.card}
            data-style={cardStyle}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            {survey.language?.enabled && (
              <div className={styles.languageControl}>
                <details
                  ref={languageMenuRef}
                  className={styles.languageMenu}
                >
                  <summary
                    className={styles.languageMenu__summary}
                    aria-label={surveyUiCopy.languageLabel}
                  >
                    <span className={styles.languageMenu__label}>
                      {surveyUiCopy.languageLabel}
                    </span>
                    <span className={styles.languageMenu__value}>
                      <span>{SURVEY_LANGUAGE_BY_CODE[selectedSurveyLanguage].englishName}</span>
                      <span>{SURVEY_LANGUAGE_BY_CODE[selectedSurveyLanguage].nativeName}</span>
                    </span>
                  </summary>
                  <div
                    className={styles.languageMenu__list}
                    role="listbox"
                    aria-label={surveyUiCopy.languageLabel}
                  >
                    {survey.language.enabledLanguages.map((language) => {
                      const definition = SURVEY_LANGUAGE_BY_CODE[language];
                      const isActive = language === selectedSurveyLanguage;
                      return (
                        <button
                          key={language}
                          type="button"
                          className={styles.languageMenu__option}
                          aria-selected={isActive}
                          role="option"
                          onClick={() => selectSurveyLanguage(language)}
                        >
                          <span>{definition.englishName}</span>
                          <span>{definition.nativeName}</span>
                        </button>
                      );
                    })}
                  </div>
                </details>
                {languageFallbackMessage && (
                  <p className={styles.languageControl__notice}>
                    {languageFallbackMessage}
                  </p>
                )}
              </div>
            )}
            {headerBlock}

            <AnimatePresence mode="wait" initial={false}>
              {/* Blocked access */}
              {stepKey === "blocked" && (
                <motion.div
                  key="step-blocked"
                  className={styles.step}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <p className={styles.step__eyebrow}>{surveyUiCopy.surveyUnavailable}</p>
                  <h1 className={styles.step__title}>{surveyUiCopy.cannotOpenSurvey}</h1>
                  <p className={styles.step__lede}>
                    {surveyUiCopy.accessMissingResponse}
                  </p>
                </motion.div>
              )}

              {/* Submitted thank-you */}
              {stepKey === "submitted" && (
                <motion.div
                  key="step-submitted"
                  className={styles.step}
                  initial={{ opacity: 0, scale: 0.96 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.32, ease: "easeOut" }}
                >
                  <div className={styles.result}>
                    <div className={styles.result__icon} aria-hidden="true">
                      ✓
                    </div>
                    <p className={styles.step__eyebrow}>{surveyUiCopy.submitted}</p>

                    {/* Authored completion blocks take precedence over
                        the legacy hardcoded copy. They live on the
                        survey as content-only blocks with
                        `pageRole: 'completion'` and were authored in
                        the editor's Completion section. When the
                        review-site CTA is enabled and conditions
                        match, we still need a short headline to lead
                        into the Share button — fall back to the
                        legacy "Help others find us" copy in that case
                        only. Otherwise authored blocks fully replace
                        the inline text. */}
                    {completionBlocks.length > 0 ? (
                      <div className={styles.questionStack}>
                        {completionBlocks.map((q) => (
                          <div key={q.id}>
                            <SurveyRenderer
                              mode="preview"
                              question={q}
                              uiStrings={surveyUiCopy}
                              brandColor={brandColor}
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <>
                        <h1 className={styles.step__title}>
	                          {submissionTitle ||
	                            (shouldDisplayReviewSiteResult
	                              ? surveyUiCopy.helpOthersFindUs
	                              : surveyUiCopy.completedTitle)}
                        </h1>
                        <p className={styles.step__lede}>
	                          {shouldDisplayReviewSiteResult
	                            ? submissionSharePrompt ||
	                              surveyUiCopy.googleReviewPrompt
	                            : surveyUiCopy.completedBody}
                        </p>
                      </>
                    )}

                    {shouldDisplayReviewSiteResult && (
                      <Button
                        variant="primary"
                        size="lg"
                        onClick={() =>
                          navigateToGoogleReview(survey?.organization?.orgGoogleUrl)
                        }
                        style={{ backgroundColor: brandColor }}
                      >
                        {surveyUiCopy.shareToGoogle}
                      </Button>
                    )}
                  </div>
                </motion.div>
              )}

              {/* Contact gate */}
              {stepKey === "contact" && (
                <motion.div
                  key="step-contact"
                  className={styles.step}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -12 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                >
                  <p className={styles.step__eyebrow}>{surveyUiCopy.beforeWeBegin}</p>
                  <h1 className={styles.step__title}>{surveyUiCopy.tellUsWhoYouAre}</h1>
                  <p className={styles.step__lede}>
                    {surveyUiCopy.contactLede}
                  </p>
                  <form
                    onSubmit={(event) => {
                      event.preventDefault();
                      void submitContactInfo();
                    }}
                  >
                    <div className={styles.fieldGroup}>
                      <div className={styles.field}>
                        <label className={styles.field__label} htmlFor="sl-name">
                          {surveyUiCopy.nameLabel}
                        </label>
                        <input
                          id="sl-name"
                          type="text"
                          placeholder={surveyUiCopy.namePlaceholder}
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          className={styles.field__input}
                        />
                      </div>
                      <div className={styles.field}>
                        <label className={styles.field__label} htmlFor="sl-email">
                          {surveyUiCopy.emailLabel}
                        </label>
                        <input
                          id="sl-email"
                          type="email"
                          placeholder={surveyUiCopy.emailPlaceholder}
                          value={email}
                          onChange={(e) => setEmail(normalizeEmailAddress(e.target.value))}
                          onBlur={handleEmailBlur}
                          className={styles.field__input}
                          aria-invalid={Boolean(emailTouched && emailError)}
                        />
                        {emailTouched && emailError && (
                          <span className={styles.fieldError}>{emailError}</span>
                        )}
                      </div>
                    </div>
                    <div className={styles.actions}>
                      <Button
                        type="submit"
                        variant="primary"
                        size="lg"
                        disabled={!isContactInfoValid || !survey || submittingContactInfo}
                        style={{ backgroundColor: brandColor }}
                      >
                        {submittingContactInfo ? surveyUiCopy.submitting : surveyUiCopy.continueButton}
                      </Button>
                    </div>
                  </form>
                </motion.div>
              )}

              {/* Questions */}
              {stepKey === "questions" && (
                <motion.div
                  key={`step-questions-${currentPage}`}
                  className={styles.step}
                  custom={pageDirection}
                  initial={{ opacity: 0, x: pageDirection > 0 ? 32 : -32 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: pageDirection > 0 ? -32 : 32 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  style={{ willChange: "transform, opacity" }}
                >
                  {SortedQuestions.length === 0 ? (
                    <>
                      <p className={styles.step__eyebrow}>{surveyUiCopy.comingSoon}</p>
                      <h1 className={styles.step__title}>
                        {surveyUiCopy.noQuestions}
                      </h1>
                      <p className={styles.step__lede}>
                        {surveyUiCopy.checkBackSoon}
                      </p>
                    </>
                  ) : visibleFlowQuestions.length === 0 ? (
                    <>
                      <p className={styles.step__eyebrow}>{surveyUiCopy.noVisibleQuestions}</p>
                      <h1 className={styles.step__title}>
                        {surveyUiCopy.noVisibleQuestions}
                      </h1>
                      <p className={styles.step__lede}>
                        {surveyUiCopy.noVisibleQuestionsBody}
                      </p>
                    </>
                  ) : (
                    <div className={styles.questionStack}>
                      <AnimatePresence initial={false}>
                        {currentPageQuestions.map((q) => (
                          <motion.div
                            key={q.id}
                            layout
                            className={styles.conditionalQuestion}
                            initial={{ height: 0, opacity: 0, filter: "blur(6px)" }}
                            animate={{ height: "auto", opacity: 1, filter: "blur(0px)" }}
                            exit={{ height: 0, opacity: 0, filter: "blur(6px)" }}
                            transition={conditionalQuestionTransition}
                            style={{ order: parseInt(q.orderId) }}
                          >
                            {renderQuestion(q)}
                            {validationErrors[q.id] ? (
                              <p className={styles.questionError} role="alert">
                                {validationErrors[q.id]}
                              </p>
                            ) : null}
                          </motion.div>
                        ))}
                      </AnimatePresence>
                    </div>
                  )}

                  <div className={styles.actions}>
                    <div className={styles.actions__row}>
                      {currentPage > 1 ? (
                        <Button
                          variant="secondary"
                          size="lg"
                          leadingVisual="ChevronLeft"
                          onClick={goToPrevPage}
                          disabled={sending}
                        >
                          {surveyUiCopy.back}
                        </Button>
                      ) : (
                        <span />
                      )}
                      {currentPage < maxPage ? (
                        <Button
                          variant="primary"
                          size="lg"
                          trailingVisual="ChevronRight"
                          style={{ backgroundColor: brandColor }}
                          onClick={goToNextPage}
                          disabled={sending}
                        >
                          {surveyUiCopy.next}
                        </Button>
                      ) : (
                        <Button
                          variant="primary"
                          size="lg"
                          trailingVisual="ChevronRight"
                          style={{ backgroundColor: brandColor }}
                          onClick={handleSubmitReview}
                          disabled={sending}
                        >
                          {sending ? surveyUiCopy.submitting : surveyUiCopy.submit}
                        </Button>
                      )}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {error && (
              <div className={styles.errorMessage} role="alert" aria-live="assertive">
                <p>{surveyUiCopy.errorPrefix} {error}</p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setError(null)}
                >
                  {surveyUiCopy.dismiss}
                </Button>
              </div>
            )}

            {footerBlock}
          </motion.section>
        </main>
      )}
    </div>
  );
}
